/**
 * Config pipelines — single map for operators + engineers.
 *
 * Two edge/runtime paths coexist on purpose. Both share Postgres as source of
 * truth; they differ on *who* reads first and *how* cache is invalidated.
 *
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ PIPELINE A — "Edge CTA / Wishlist"                                      │
 * │ Consumer: Cloudflare Worker (first HTML / geo CTA)                      │
 * │ Store:    Postgres wishlist_geo_config + site_settings slices           │
 * │ Cache:    Worker CTA_CONFIG KV (L2) + api/lib/kvCache version-gated     │
 * │ APIs:     GET /api/wishlist-config (+ version endpoint)                 │
 * │ Admin:    /admin/wishlist-config                                        │
 * │ Rule:     bump version on write → never serve stale payload             │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ PIPELINE B — "DualCTA conversion" (home IG / free Telegram / VIP)       │
 * │ Consumer: SPA DualCTAVideoLanding + InvitacionExclusivaMux              │
 * │ Store:    Postgres premium_invite_config + site_settings intl columns   │
 * │ Cache:    HTTP no-store + optional versioned kvCache (dual_cta_public)  │
 * │ APIs:     GET /api/premium-invite-config, GET /api/site-settings        │
 * │ Admin:    /admin/premium-invite-config + /admin/country-templates       │
 * │ Rule:     frescura de conversión > TTL largo; KV solo version-gated     │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * Do NOT put free_telegram_url only in Worker env or only in wishlist KV —
 * DualCTA reads Pipeline B. Do NOT expect DualCTA VIP charge from Worker KV.
 */

export const CONFIG_PIPELINE_EDGE_WISHLIST = "edge_wishlist" as const;
export const CONFIG_PIPELINE_DUAL_CTA = "dual_cta_conversion" as const;

export type ConfigPipelineId =
  | typeof CONFIG_PIPELINE_EDGE_WISHLIST
  | typeof CONFIG_PIPELINE_DUAL_CTA;

/** Operator-facing summary (Centro de control, docs). */
export const CONFIG_PIPELINE_MATRIX: Record<
  ConfigPipelineId,
  {
    title: string;
    consumer: string;
    postgres: string[];
    cache: string;
    admin: string[];
    publicApis: string[];
    maxStaleHint: string;
  }
> = {
  [CONFIG_PIPELINE_EDGE_WISHLIST]: {
    title: "Edge CTA / Wishlist",
    consumer: "Cloudflare Worker + BaseTemplate geo CTAs",
    postgres: ["wishlist_geo_config", "site_settings (template / hide_hero slices)"],
    cache: "CF KV CTA_CONFIG + kvCache version-gated (TTL ~600s, bump on admin save)",
    admin: ["/admin/wishlist-config"],
    publicApis: ["/api/wishlist-config", "/api/wishlist-config-version"],
    maxStaleHint: "Version bump → next read fresh; edge L2 ≤10 min if version matches",
  },
  [CONFIG_PIPELINE_DUAL_CTA]: {
    title: "DualCTA / Invitación conversion (home + exclusive)",
    consumer:
      "SPA DualCTAVideoLanding + InvitacionExclusivaMux (/invitacion-exclusive) + VIP Embedded",
    postgres: [
      "premium_invite_config (free URL, dual_cta free flags, mux overlay, CTA labels)",
      "site_settings (template, mux by country, VIP pricing default/map, dual_cta free by country)",
      "membership_products (VIP Price + metadata.promo fallback)",
    ],
    cache:
      "site-settings max-age=15 + in-process 12s; dual_cta_public versioned kvCache (short TTL); client hard-refetch on admin publish",
    admin: ["/admin/premium-invite-config", "/admin/country-templates"],
    publicApis: ["/api/premium-invite-config", "/api/site-settings"],
    maxStaleHint:
      "Admin save → invalidatePublicSiteSettingsCache + dual_cta KV bump + SPA hard-refetch; F5 siempre fresco",
  },
};

/**
 * Free Telegram show/hide priority (DualCTA) — must match
 * `resolveInternationalHomeDualCtaFreeTelegramEnabled` + DualCTA gates.
 */
export const DUAL_CTA_FREE_SHOW_PRIORITY = [
  "hide_global_hero_cta → hide free+VIP",
  "premium_invite_config.free_telegram_enabled === false → hide free site-wide",
  "country === AR → hide free (hardcode)",
  "site_settings dual_cta free by country[ISO2]",
  "site_settings dual_cta free global",
  "premium_invite_config.dual_cta_free_telegram_enabled",
  "null → auto if URL resolves",
] as const;

/**
 * Free Telegram URL priority — must match `resolveFreeJoinTelegramUrl*`.
 */
export const DUAL_CTA_FREE_URL_PRIORITY = [
  "premium_invite_config.free_telegram_url_by_country[ISO2]",
  "premium_invite_config.free_telegram_url",
  "VITE_PREMIUM_SIMPLE_FREE_TELEGRAM_URL",
  "VITE_TELEGRAM_FREE_GROUP_URL",
  "VITE_BRAND_TELEGRAM_URL / BRAND_TELEGRAM_URL",
] as const;

/**
 * VIP charge priority — DualCTA + InvitacionExclusivaMux + Bio VIP surfaces.
 * Client: resolveInternationalHomeVipCheckoutPricing
 * Server: api/lib/resolveVipChargeConfig (create-subscription-checkout)
 * Worker KV never stores VIP prices.
 */
export const DUAL_CTA_VIP_CHARGE_PRIORITY = [
  "body.priceId + body.promotionCodeId (SPA DualCTA/Invitacion/Bio after admin resolve)",
  "site_settings country map ISO2.stripePriceId (+ promo if applyDiscount)",
  "site_settings compact vipPricingDefault (public API when all countries share one offer)",
  "membership_products plan_tier=vip stripe_price_id + metadata.promotion_code_id",
  "FALLBACK_VIP_STRIPE_PRICE_ID + FALLBACK FIRSTANA promo (price_1TsRw… / promo_1TsRx…)",
] as const;

/**
 * Worker template inject vs SPA ownership (coherence).
 * SPA React Router always wins for dedicated paths; Worker must not lie in cookies.
 */
export const WORKER_SPA_TEMPLATE_COHERENCE = {
  "/": {
    spa: "Index → resolveEffectiveHomeTemplate(site-settings); AR=BaseTemplate unless AR override",
    worker: "COUNTRY_TEMPLATE_MAP + intl override from wishlist-config; AR=BaseTemplate",
  },
  "/invitacion-exclusive": {
    spa: "InvitacionExclusivaMux (always) — canonical AR exclusive CTA",
    worker:
      "MUST inject InvitacionExclusivaMux; hideCta=false; no country-template steal (v2.6.7+)",
  },
  "/invitacion-exclusiva": {
    spa: "InvitacionExclusivaMux (legacy alias)",
    worker: "same as /invitacion-exclusive",
  },
  "/invitacion-premium": {
    spa: "InvitacionExclusivaMux (always)",
    worker: "same as /invitacion-exclusive",
  },
  "/video-landing": {
    spa: "DualCTAVideoLanding",
    worker: "geo map / DualCTA default",
  },
} as const;

/**
 * Funnel entry map (anaafernandez brand) — admin must match this product design.
 *
 * | Audience        | Home `/`              | VIP membership entry                         |
 * |-----------------|-----------------------|----------------------------------------------|
 * | Argentina       | BaseTemplate          | Wishlist `community` CTA → /invitacion-exclusive |
 * | International   | DualCTAVideoLanding   | CTA VIP on same page (init)                  |
 *
 * Admin owners:
 * - Home template / VIP price: /admin/country-templates
 * - AR hero CTA geo: /admin/wishlist-config (community = Exclusivo → exclusive URL)
 */
export const BRAND_FUNNEL_ENTRY_COHERENCE = {
  argentina: {
    homeTemplate: "BaseTemplate",
    homeAdmin: "/admin/country-templates (no AR override → Worker BaseTemplate)",
    exclusiveCtaAdmin: "/admin/wishlist-config",
    exclusiveCtaType: "community",
    exclusiveCtaLabel: "Exclusivo",
    exclusiveUrl: "/invitacion-exclusive",
    exclusiveComponent: "InvitacionExclusivaMux",
    vipPricing: "same site_settings vipPricingDefault as DualCTA",
  },
  international: {
    homeTemplate: "DualCTAVideoLanding",
    homeAdmin: "/admin/country-templates (global + by-country)",
    exclusiveUrlOnInit: false,
    vipOnHome: true,
    vipPricing: "site_settings vipPricingDefault / by-country",
    optionalDeepLink: "/invitacion-exclusive (campaigns; same VIP)",
  },
} as const;
